/**
 * The four task mutations, in one place.
 *
 * WHY ONE PLACE: accept, reject, start and complete each have a rule that is
 * easy to get subtly wrong, and having two screens implement them twice is how
 * they drift.
 *
 *   ACCEPT   is a RACE. The server does a compare-and-set on `claim_version`
 *            and the loser gets `ALREADY_CLAIMED` — which is a NORMAL outcome,
 *            not an error. It must be reported calmly ("việc này vừa có người
 *            khác nhận") and the task list refetched, never shown as a failure.
 *            Not queueable offline: an offer that expired ten minutes ago
 *            cannot be accepted retroactively.
 *
 *   REJECT   needs a free-text reason in the `x-action-reason` HEADER as well
 *            as a `reasonCode` in the body. Sending only the body is a 400.
 *
 *   COMPLETE is the row KPI is computed from, so its response carries
 *            `kpiCounted` / `kpiRejectReason` and the UI must surface both —
 *            an event that silently earned nothing destroys trust faster than
 *            one that earned nothing loudly. It IS queueable offline.
 *
 * NOTHING here ever awards points. The server writes zero on every event and
 * the KPI engine scores later; any client-side "+5 điểm" would be a lie.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { ApiError, newIdempotencyKey } from '@sonauto/api-client';
import { api } from './api';
import { queryKeys } from './queries';
import type {
  RejectReasonCode,
  TaskAcceptResponse,
  TaskCompleteRequest,
  TaskCompleteResponse,
  TaskRejectResponse,
  TaskStartResponse,
} from './contracts';
import { enqueueTaskComplete } from './outbox';
import { KPI_REJECT_LABEL } from './labels';

function useInvalidate(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    void queryClient.invalidateQueries({ queryKey: queryKeys.home });
  };
}

export function useAcceptTask(): UseMutationResult<TaskAcceptResponse, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (taskId: string) => api.acceptTask(taskId, newIdempotencyKey()),
    onSettled: invalidate,
  });
}

export function useRejectTask(): UseMutationResult<
  TaskRejectResponse,
  Error,
  { taskId: string; reasonCode: RejectReasonCode; reason: string }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ taskId, reasonCode, reason }) => api.rejectTask(taskId, reasonCode, reason),
    onSettled: invalidate,
  });
}

export function useStartTask(): UseMutationResult<TaskStartResponse, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (taskId: string) => api.startTask(taskId, newIdempotencyKey()),
    onSettled: invalidate,
  });
}

export interface CompleteInput {
  readonly taskId: string;
  readonly taskTitle: string;
  readonly body: TaskCompleteRequest;
}

/** `queued: true` means it went to the outbox rather than the server. */
export type CompleteOutcome =
  | { readonly queued: false; readonly response: TaskCompleteResponse }
  | { readonly queued: true };

export function useCompleteTask(): UseMutationResult<CompleteOutcome, Error, CompleteInput> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ taskId, taskTitle, body }): Promise<CompleteOutcome> => {
      // The one write this app queues besides attendance. The idempotency key
      // is the outbox row id, so a replay is the same logical completion.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await enqueueTaskComplete(taskId, taskTitle, body);
        return { queued: true };
      }
      try {
        return { queued: false, response: await api.completeTask(taskId, body, newIdempotencyKey()) };
      } catch (error) {
        if (error instanceof ApiError && error.isConnectivityError) {
          await enqueueTaskComplete(taskId, taskTitle, body);
          return { queued: true };
        }
        throw error;
      }
    },
    onSettled: invalidate,
  });
}

/**
 * Vietnamese sentence for the `kpiCounted` / `kpiRejectReason` pair, or null
 * when the event counted normally and there is nothing to explain.
 */
export function kpiNotice(counted: boolean, reason: string | null): string | null {
  if (counted) return null;
  if (reason !== null && reason in KPI_REJECT_LABEL) {
    return KPI_REJECT_LABEL[reason as keyof typeof KPI_REJECT_LABEL];
  }
  return 'Thao tác đã được ghi nhận nhưng không được tính điểm KPI.';
}

/** `ALREADY_CLAIMED` is expected, not exceptional — screens branch on this. */
export function isAlreadyClaimed(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'ALREADY_CLAIMED';
}
